import { useState } from 'react';
import { BadgePercent, CheckCircle2, ChevronDown, Lock } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format/money';
import { offerHeadline, type Offer } from '@/features/campaigns/utils/couponMath';

type OffersCardProps = {
  offers: Offer[];
  loading: boolean;
  /** The code that is applied right now ('' when none). */
  appliedCode: string;
  /** What the applied code saves on the current order. */
  saving: number;
  onApply: (code: string) => void;
  onRemove: () => void;
  /** Applies a typed code; returns a short message when it cannot be used. */
  onApplyTyped: (code: string) => string | null;
};

/**
 * Offers next to the bill: the best saving up front with one big Apply button, the rest a tap
 * away, and offers that unlock with a slightly bigger order say how much more is needed.
 */
export function OffersCard({ offers, loading, appliedCode, saving, onApply, onRemove, onApplyTyped }: OffersCardProps) {
  const [showAll, setShowAll] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState('');
  const [typedError, setTypedError] = useState<string | null>(null);

  if (loading) {
    return (
      <section className="camp-section text-left" aria-busy>
        <p className="m-0 text-sm text-[var(--dash-muted)]">Finding offers for you…</p>
      </section>
    );
  }
  if (!offers.length && !appliedCode) return null;

  const applied = offers.find((offer) => offer.code === appliedCode.toUpperCase());
  const best = offers.find((offer) => offer.discount > 0 && offer.code !== appliedCode.toUpperCase());
  const others = offers.filter((offer) => offer !== best && offer.code !== appliedCode.toUpperCase());

  const submitTyped = () => {
    const problem = onApplyTyped(typed);
    setTypedError(problem);
    if (!problem) {
      setTyped('');
      setTyping(false);
    }
  };

  return (
    <section className="camp-section text-left" aria-label="Offers">
      <div className="mb-3 flex items-center gap-2">
        <BadgePercent className="h-4 w-4 text-[var(--dash-success)]" />
        <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">Offers for you</h2>
      </div>

      {appliedCode ? (
        <div className="flex items-center gap-3 rounded-xl border border-[var(--dash-success)]/30 bg-[var(--dash-success-soft)] p-3">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-[var(--dash-success)]" />
          <div className="min-w-0 flex-1">
            <p className="m-0 text-sm font-bold text-[var(--dash-success)]">
              {saving > 0 ? `You save ${formatMoney(saving)}` : `${appliedCode.toUpperCase()} applied`}
            </p>
            <p className="m-0 mt-0.5 truncate text-xs text-[var(--dash-muted)]">
              {appliedCode.toUpperCase()}
              {applied ? ` · ${offerHeadline(applied.coupon)}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 border-none bg-transparent p-1 text-xs font-semibold text-[var(--dash-muted)] underline"
          >
            Remove
          </button>
        </div>
      ) : best ? (
        <div className="flex items-center gap-3 rounded-xl border border-[var(--dash-success)]/30 bg-[var(--dash-success-soft)] p-3">
          <div className="min-w-0 flex-1">
            <p className="m-0 text-[10px] font-bold tracking-wide text-[var(--dash-success)] uppercase">Best offer</p>
            <p className="m-0 mt-0.5 text-sm font-bold text-[var(--dash-ink)]">Save {formatMoney(best.discount)}</p>
            <p className="m-0 mt-0.5 truncate text-xs text-[var(--dash-muted)]">
              {best.code} · {offerHeadline(best.coupon)}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onApply(best.code)}
            className="shrink-0 rounded-lg bg-[var(--dash-success)] px-4 py-2 text-sm font-bold text-white"
          >
            Apply
          </button>
        </div>
      ) : null}

      {others.length > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            aria-expanded={showAll}
            className="mt-3 flex w-full items-center justify-between border-none bg-transparent p-0 text-xs font-semibold text-[var(--dash-brand)]"
          >
            {showAll ? 'Hide offers' : `View ${others.length} more offer${others.length === 1 ? '' : 's'}`}
            <ChevronDown className={`h-4 w-4 transition-transform ${showAll ? 'rotate-180' : ''}`} />
          </button>

          {showAll ? (
            <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
              {others.map((offer) => (
                <li
                  key={offer.code}
                  className="flex items-center gap-3 rounded-xl border border-[var(--dash-line)] bg-white p-2.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-sm font-bold text-[var(--dash-ink)]">
                      {offer.discount > 0 ? `Save ${formatMoney(offer.discount)}` : offerHeadline(offer.coupon)}
                    </p>
                    <p className="m-0 mt-0.5 truncate text-xs text-[var(--dash-muted)]">
                      {offer.code}
                      {offer.discount > 0 ? ` · ${offerHeadline(offer.coupon)}` : ''}
                    </p>
                    {offer.discount <= 0 ? (
                      <p className="m-0 mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                        <Lock className="h-3 w-3" />
                        Add {formatMoney(offer.unlockNeeds)} more to unlock
                      </p>
                    ) : null}
                  </div>
                  {offer.discount > 0 ? (
                    <button
                      type="button"
                      onClick={() => onApply(offer.code)}
                      className="shrink-0 rounded-lg border border-[var(--dash-success)] bg-white px-3 py-1.5 text-xs font-bold text-[var(--dash-success)]"
                    >
                      Apply
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      <div className="mt-3">
        {typing ? (
          <div>
            <div className="flex gap-2">
              <input
                value={typed}
                onChange={(event) => {
                  setTyped(event.target.value.toUpperCase());
                  setTypedError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') submitTyped();
                }}
                placeholder="Enter code"
                aria-label="Promo code"
                aria-invalid={Boolean(typedError)}
                className="camp-input min-w-0 flex-1 rounded-lg border border-[var(--dash-line)] bg-white px-3 py-2 text-sm uppercase"
              />
              <button
                type="button"
                onClick={submitTyped}
                className="shrink-0 rounded-lg bg-[var(--dash-brand)] px-4 py-2 text-sm font-semibold text-white"
              >
                Apply
              </button>
            </div>
            {typedError ? <p className="m-0 mt-1 text-xs text-red-600">{typedError}</p> : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setTyping(true)}
            className="border-none bg-transparent p-0 text-xs font-semibold text-[var(--dash-muted)] underline"
          >
            Have a code?
          </button>
        )}
      </div>
    </section>
  );
}
